import React, { Component } from "react";
import { Button, Form, Input, notification, Select, Space, Spin, Avatar, Modal } from "antd";
import { FormattedMessage, injectIntl } from "react-intl";
import styled, { withTheme } from "styled-components";
import AppContext from "../../../AppContext";

class UpdateSecurity extends Component {
  static contextType = AppContext;

  securityChange = React.createRef();

  constructor(props) {
    super(props);
    this.state = {
      saving: false,
      show: false,
      filled: false,
      securityEditing: false,
      password: false,
    };
  }

  componentDidUpdate = prevProps => {
    if (this.props.reset !== prevProps.reset && this.props.reset && this.securityChange.current?.resetFields) {
      this.securityChange.current.resetFields();
      this.setState({
        saving: false,
        show: false,
        filled: false,
        securityEditing: false,
        password: false,
      });
    }
  };

  checkAllFields = () => {
    const fields = this.securityChange.current?.getFieldsValue();
    const keys = Object.keys(fields ?? {});
    let inputs = {
      security_question: undefined,
      new_security_question: undefined,
      new_security_answer: undefined,
      confirm_new_security_answer: undefined,
    };
    let isEditing = false;
    for (let i = 0; i < keys?.length; i++) {
      if (Object.keys(inputs).includes(keys?.[i])) {
        inputs[keys[i]] = fields?.[keys?.[i]];
        if (!isEditing && (fields?.[keys?.[i]] ?? "") !== "") {
          this.setState({ securityEditing: true });
        }
        isEditing = true;
      }
    }
    let reset =
      (inputs.security_question === "" || inputs.security_question === undefined) &&
      (inputs.new_security_question === "" || inputs.new_security_question === undefined) &&
      (inputs.new_security_answer === "" || inputs.new_security_answer === undefined) &&
      (inputs.confirm_new_security_answer === "" || inputs.confirm_new_security_answer === undefined);
    let showEverything =
      inputs.security_question !== undefined &&
      inputs.new_security_question !== undefined &&
      inputs.new_security_answer !== undefined &&
      inputs.confirm_new_security_answer !== undefined &&
      inputs.new_security_answer === inputs.confirm_new_security_answer;
    if (!reset) {
      if (this.state.show !== showEverything) {
        this.setState({ show: showEverything });
        if (!showEverything && this.state.filled) {
          this.setState({ filled: false });
        }
      }
      if (showEverything && this.checkNoErrors() && this.state.password) {
        this.setState({ filled: true });
      } else {
        this.setState({ filled: false });
      }
    }
    if (reset && this.securityChange.current?.resetFields) {
      this.securityChange.current.resetFields();
      if (this.state.show) {
        this.setState({ show: false });
      }
      if (this.state.filled) {
        this.setState({ filled: false });
      }
      this.setState({ securityEditing: false });
    }
  };

  compareAnswers = async (rule, value) => {
    const fields = this.securityChange.current?.getFieldsValue();
    if (value === fields?.new_security_answer) {
      return Promise.resolve();
    } else {
      return Promise.reject();
    }
  };

  checkNoErrors = () => {
    const errors = this.securityChange.current?.getFieldsError() ?? [];
    for (let i = 0; i < errors.length; i++) {
      if ((errors?.[i]?.errors?.length ?? 0) > 0) {
        return false;
      }
    }
    return true;
  };

  onSubmit = async () => {
    this.setState({ saving: true });
    let results = await this.props.changeSQ({
      ...this.securityChange.current?.getFieldsValue(),
    });
    if (results === true) {
      this.props.reloadUserQuestions();
      if (this.securityChange.current?.resetFields) {
        this.securityChange.current.resetFields();
      }
      notification.success({
        message: <div className="text-capitalize-first">{this.props.intl.formatMessage({ id: "success" })}</div>,
        duration: 5,
      });
      this.setState({
        saving: false,
        show: false,
        filled: false,
        securityEditing: false,
        password: false,
      });
    } else {
      this.setState({
        saving: false,
      });
      notification.error({
        message: (
          <div className="text-capitalize-first">
            {this.props.intl.formatMessage({
              id: "there was a problem with your current password or authorisation.",
            })}
          </div>
        ),
      });
    }
  };

  getQuestions = () => {
    return (this.props.userQuestions ?? []).map(question =>
      Object.fromEntries(
        new Map([
          ["label", this.props.intl.formatMessage({ id: question })],
          ["value", question],
        ])
      )
    );
  };

  getAllQuestions = () => {
    return (this.props.allQuestions ?? []).map(question =>
      Object.fromEntries(
        new Map([
          ["label", this.props.intl.formatMessage({ id: question })],
          ["value", question],
        ])
      )
    );
  };

  onCancel = () => {
    const { confirm } = Modal;
    const { intl } = this.props;
    confirm({
      width: 500,
      title: (
        <div className="text-capitalize-first">
          {intl.formatMessage({ id: "are you sure you want to cancel these changes?" })}
        </div>
      ),
      content: (
        <div className="text-capitalize-first">
          {intl.formatMessage({ id: "if you continue, changes will be lost." })}
        </div>
      ),
      okText: <div className="text-capitalize-first">{intl.formatMessage({ id: "yes" })}</div>,
      okButtonProps: { type: "primary" },
      cancelText: <div className="text-capitalize-first">{intl.formatMessage({ id: "no" })}</div>,
      cancelButtonProps: { type: "default" },
      onOk: async () => {
        if (this.securityChange.current?.resetFields) {
          this.securityChange.current.resetFields();
          await this.setState({
            saving: false,
            show: false,
            filled: false,
            securityEditing: false,
            password: false,
          });
        }
      },
    });
  };

  checkDifferentSQ = async (rule, value) => {
    if (rule?.required) {
      if (this.props?.userQuestions?.filter(question => question === value)?.length === 0) {
        return Promise.resolve();
      } else {
        return Promise.reject();
      }
    }
    return Promise.resolve();
  };

  getCurrentQuestions = () => {
    return (
      <Form.Item
        name="security_question"
        label={
          <span className="text-capitalize">
            <FormattedMessage id="question" />
          </span>
        }
        rules={[
          {
            required: true,
            message: (
              <div className="text-capitalize-first">
                {this.props.intl.formatMessage({ id: "select question to update" })}
              </div>
            ),
            validator: () => {
              if (this.securityChange.current?.validateFields) {
                this.securityChange.current.validateFields(["new_security_question"]);
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <Select
          options={this.getQuestions()}
          allowClear
          placeholder={
            <div className="text-capitalize-first">
              {this.props.intl.formatMessage({ id: "select question to update" })}
            </div>
          }
        />
      </Form.Item>
    );
  };

  getNewQuestion = () => {
    return (
      <>
        <Form.Item
          name="new_security_question"
          label={
            <span className="text-capitalize">
              <FormattedMessage id="question" />
            </span>
          }
          rules={[
            {
              required: true,
              message: (
                <div className="text-capitalize-first">
                  {this.props.intl.formatMessage({ id: "select new security question" })}
                </div>
              ),
              validator: (rule, value) => this.checkDifferentSQ(rule, value),
            },
          ]}
        >
          <Select
            options={this.getAllQuestions()}
            allowClear
            placeholder={
              <div className="text-capitalize-first">
                {this.props.intl.formatMessage({ id: "select new security question" })}
              </div>
            }
          />
        </Form.Item>
        <Form.Item
          name="new_security_answer"
          label={
            <span className="text-capitalize">
              <FormattedMessage id="response" />
            </span>
          }
          rules={[
            {
              required: true,
              message: this.props.intl.formatMessage({ id: "Enter response" }),
            },
          ]}
        >
          <Input.Password
            type="password"
            placeholder={this.props.intl.formatMessage({ id: "Enter response" })}
            autoComplete="off"
          />
        </Form.Item>
        <Form.Item
          name="confirm_new_security_answer"
          label={
            <span className="text-capitalize">
              <FormattedMessage id="confirm response" />
            </span>
          }
          dependencies={['new_security_answer']}
          rules={[
            {
              required: true,
              message: this.props.intl.formatMessage({ id: "Responses do not match" }),
              validator: (rule, value) => this.compareAnswers(rule, value),
            },
          ]}
        >
          <Input.Password
            type="password"
            placeholder={this.props.intl.formatMessage({ id: "Confirm response" })}
            autoComplete="off"
          />
        </Form.Item>
      </>
    );
  };

  render() {
    return (
      <Wrapper>
        <Spin spinning={this.state.saving}>
          <Form ref={this.securityChange} layout="vertical" onFieldsChange={this.checkAllFields} requiredMark={false}>
            <div className="text-capitalize-first">
              <FormattedMessage id="Update your security question and response by following the steps below. You will be asked to enter your password to authorise the security update." />
            </div>
            <div className="stepHeader">
              <Avatar size="small" shape="circle">
                1
              </Avatar>
              <div className="text-capitalize">
                <FormattedMessage id="current" />
              </div>
            </div>
            {this.getCurrentQuestions()}
            <div className="stepHeader step2">
              <Avatar size="small" shape="circle">
                2
              </Avatar>
              <div className="text-capitalize">
                <FormattedMessage id="new" />
              </div>
            </div>
            {this.getNewQuestion()}
            {this.state.show && (
              <>
                <div className="stepHeader step2">
                  <Avatar size="small" shape="circle">
                    3
                  </Avatar>
                  <div className="text-capitalize">
                    <FormattedMessage id="authorise" />
                  </div>
                </div>
                <Form.Item
                  name="current_password"
                  label={
                    <span className="text-capitalize">
                      <FormattedMessage id="password" />
                    </span>
                  }
                  rules={[
                    {
                      required: true,
                      message: this.props.intl.formatMessage({ id: "Enter to authorise security update" }),
                    },
                  ]}
                >
                  <Input.Password
                    placeholder={this.props.intl.formatMessage({ id: "Enter to authorise security update" })}
                    onChange={e => this.setState({ password: e?.target?.value ? true : false })}
                  />
                </Form.Item>
                <Form.Item className="lastItem" id="thePopupContainer">
                  <div></div>
                  <Space direction="vertical">
                    <Space align="end">
                      <Button disabled={!this.state.securityEditing} onClick={this.onCancel}>
                        <div className="text-capitalize-first">
                          <FormattedMessage id="cancel" />
                        </div>
                      </Button>
                      <Button disabled={!this.state.filled} type="primary" onClick={this.onSubmit}>
                        <div className="text-capitalize-first">
                          <FormattedMessage id="update" />
                        </div>
                      </Button>
                    </Space>
                  </Space>
                </Form.Item>
              </>
            )}
          </Form>
        </Spin>
      </Wrapper>
    );
  }
}

const Wrapper = styled.div`
  .ant-form-item-label > label {
    display: block;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    padding-right: 1em;
  }
  .ant-form-item {
    margin: 1em 0;
    width: 100%;
    max-width: 308px;
    margin-left: 40px;
    .ant-form-item-label {
      width: 280px;
      text-align: left;
    }
  }
  .ant-tooltip-disabled-compatible-wrapper {
    width: 100%;
  }
  .validationPassword {
    margin: 1em;
  }
  .lastItem {
    padding-top: 40px;
    .ant-form-item-control-input-content {
      display: grid;
      grid-template-columns: 0px auto;
      .ant-popover-buttons {
        .ant-btn {
          width: auto;
        }
      }
    }
  }
  .ant-avatar-circle {
    margin-right: 1.1em;
    background: ${props => props.theme.color.purple["purple-3"]};
  }
  .stepHeader {
    width: 100%;
    font-weight: ${props => props.theme.text.bold};
    margin-top: 1em;
    margin-bottom: 1em;
    display: flex;
    padding-top: 24px;
  }
  .step2 {
    padding-top: 40px;
  }
  .passwordValidatorContainer {
    display: grid;
    grid-template-columns: auto 70px;
    margin-left: 30px;
    .validationPassword {
      display: grid;
    }
  }
  .hide-div {
    display: none;
  }

  .popconfirmSave {
    width: 100%;
  }
`;

export default withTheme(injectIntl(UpdateSecurity));
